import React, { useEffect, useMemo, useState } from 'react';
import { XMarkIcon, ArrowPathIcon, DocumentDuplicateIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { PAYMENT_ACCOUNT_DETAILS, PAYMENT_GENERATION_STEPS, CUSTOMER_CARE_WHATSAPP } from '../../constants/payment';
import { compressImageToTargetKB } from '../../utils/imageCompression';
import { buildWhatsAppPaymentMessage, generatePaymentReference } from '../../utils/paymentMessage';

const readClientEnv = (key) => {
  const rawValue = process.env[key];
  if (typeof rawValue !== 'string') {
    return '';
  }

  return rawValue.replace(/^['"]|['"]$/g, '').trim();
};

const cloudinaryCloudName = readClientEnv('REACT_APP_CLOUDINARY_CLOUD_NAME');
const cloudinaryUploadPreset = readClientEnv('REACT_APP_CLOUDINARY_UPLOAD_PRESET');

const PaymentModal = ({ isOpen, onClose, product }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [generationDone, setGenerationDone] = useState(false);
  const [referenceId, setReferenceId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [optimizedFile, setOptimizedFile] = useState(null);
  const [optimizedPreviewUrl, setOptimizedPreviewUrl] = useState('');
  const [proofUrl, setProofUrl] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [uploadValidationMessage, setUploadValidationMessage] = useState('');
  const [submitValidationMessage, setSubmitValidationMessage] = useState('');
  const [isCompressing, setIsCompressing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const isCloudinaryConfigured = Boolean(cloudinaryCloudName && cloudinaryUploadPreset);
  const shortProductTitle = (product?.title || '').length > 56
    ? `${product.title.slice(0, 56)}...`
    : product?.title;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setCurrentStep(0);
    setGenerationDone(false);
    setReferenceId(generatePaymentReference());
    setSelectedFile(null);
    setOptimizedFile(null);
    setOptimizedPreviewUrl('');
    setProofUrl('');
    setStatusMessage('');
    setErrorMessage('');
    setUploadValidationMessage('');
    setSubmitValidationMessage('');

    const interval = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev >= PAYMENT_GENERATION_STEPS.length - 1) {
          clearInterval(interval);
          setGenerationDone(true);
          return prev;
        }
        return prev + 1;
      });
    }, 1300);

    return () => clearInterval(interval);
  }, [isOpen]);

  const progressPercentage = useMemo(
    () => Math.round(((currentStep + 1) / PAYMENT_GENERATION_STEPS.length) * 100),
    [currentStep],
  );

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', cloudinaryUploadPreset);
    formData.append('folder', 'kambihome/proof-of-transfer');

    const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudinaryCloudName}/image/upload`, {
      method: 'POST',
      body: formData,
    });

    const payload = await response.json();

    if (!response.ok) {
      const cloudinaryError = payload?.error?.message;
      throw new Error(cloudinaryError || 'Unable to upload proof to Cloudinary. Please try again.');
    }

    return payload.secure_url;
  };

  const handleFileSelection = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setErrorMessage('');
    setStatusMessage('');
    setUploadValidationMessage('');
    setSubmitValidationMessage('');
    setSelectedFile(file);
    setProofUrl('');
    setOptimizedFile(null);
    setOptimizedPreviewUrl('');

    if (!isCloudinaryConfigured) {
      setUploadValidationMessage('Cloudinary env is missing. Set .env vars and restart/rebuild.');
      return;
    }

    setIsCompressing(true);
    setIsUploading(false);
    try {
      const compressed = await compressImageToTargetKB(file, 50);
      setOptimizedFile(compressed);
      const previewUrl = URL.createObjectURL(compressed);
      setOptimizedPreviewUrl(previewUrl);

      setIsCompressing(false);
      setStatusMessage('Optimization complete. Uploading proof to secure cloud storage...');
      setIsUploading(true);
      const url = await uploadToCloudinary(compressed);
      setProofUrl(url);
      setStatusMessage('Proof optimized and uploaded successfully. You can now submit your order.');
    } catch (error) {
      const errorMessageText = error.message || 'Unable to process proof. Please try another file.';
      if (errorMessageText.toLowerCase().includes('upload preset not found')) {
        setUploadValidationMessage('Preset not found in this Cloudinary account. Confirm cloud name + unsigned preset.');
      } else {
        setErrorMessage(errorMessageText);
      }
    } finally {
      setIsCompressing(false);
      setIsUploading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (optimizedPreviewUrl) {
        URL.revokeObjectURL(optimizedPreviewUrl);
      }
    };
  }, [optimizedPreviewUrl]);

  const handleSubmitToWhatsApp = () => {
    setSubmitValidationMessage('');
    if (!proofUrl) {
      setSubmitValidationMessage('Upload proof to Cloudinary before submitting to customer care.');
      return;
    }

    const message = buildWhatsAppPaymentMessage({
      productTitle: product.title,
      productPrice: product.formattedPrice,
      currencyCode: product.currencyCode,
      productUrl: product.productUrl,
      productImage: product.productImage,
      proofUrl,
      referenceId,
      shippingCompany: product.shippingCompany,
      shippingFee: product.shippingFee,
      shippingEta: product.shippingEta,
      totalCost: product.totalCost,
    });

    window.open(`https://wa.me/${CUSTOMER_CARE_WHATSAPP}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleCopy = async (value, successText) => {
    await navigator.clipboard.writeText(value);
    setStatusMessage(successText);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-slate-950/70 backdrop-blur-sm p-3 sm:p-4 overflow-y-auto" role="presentation">
      <div className="relative my-4 sm:my-6 w-full max-w-2xl max-h-[92vh] rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden flex flex-col" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
        <div className="bg-gradient-to-r from-green-600 to-emerald-500 px-6 py-4 text-white">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-xl font-semibold">Complete Your Payment</h3>
              <p className="text-sm text-emerald-50 mt-1">Review details and pay securely.</p>
            </div>
            <button onClick={onClose} className="rounded-full p-1 hover:bg-white/20 transition" aria-label="Close payment modal">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-5 overflow-y-auto">
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="text-xs font-medium text-slate-500 mb-2">Product to pay for</p>
            <div className="flex items-center gap-3">
              <img
                src={product.productImage}
                alt={product.title}
                className="h-14 w-14 rounded-md object-cover border border-slate-200"
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{shortProductTitle}</p>
                <p className="text-sm text-emerald-700 font-semibold">
                  {product.totalCost}
                </p>
              </div>
            </div>
          </div>

          {!generationDone ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="flex items-center gap-2 text-emerald-700 font-medium">
                <ArrowPathIcon className="h-5 w-5 animate-spin" />
                Preparing payment details
              </div>
              <p className="text-sm text-slate-700 mt-2">{PAYMENT_GENERATION_STEPS[currentStep]}</p>
              <div className="mt-3 h-2 w-full rounded bg-emerald-100">
                <div className="h-2 rounded bg-emerald-500 transition-all duration-500" style={{ width: `${progressPercentage}%` }} />
              </div>
              <p className="text-xs text-slate-500 mt-2">Progress: {progressPercentage}%</p>
            </div>
          ) : (
            <>
              <div className="rounded-xl border border-slate-200 p-4 bg-slate-50">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-slate-600">Reference ID</p>
                  <button
                    className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 text-xs font-medium"
                    onClick={() => handleCopy(referenceId, 'Reference copied.')}
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" /> Copy
                  </button>
                </div>
                <p className="font-semibold text-slate-800">{referenceId}</p>
              </div>

              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <h4 className="font-semibold text-slate-800 mb-2">Payment summary</h4>
                <div className="space-y-1 text-sm text-slate-700">
                  <p>Item: <span className="font-semibold">{product.formattedPrice} {product.currencyCode || ''}</span></p>
                  <p>Shipping: <span className="font-semibold">{product.shippingFee}</span></p>
                  <p className="text-emerald-700 font-bold">Total: {product.totalCost}</p>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 p-4">
                <h4 className="font-semibold text-slate-800 mb-3">Pay to this account</h4>
                <div className="grid sm:grid-cols-3 gap-3 text-sm">
                  <div className="rounded-lg bg-slate-50 p-3">
                    <p className="text-slate-500">Account Name</p>
                    <p className="font-semibold text-slate-800">{PAYMENT_ACCOUNT_DETAILS.accountName}</p>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3">
                    <p className="text-slate-500">Bank</p>
                    <p className="font-semibold text-slate-800">{PAYMENT_ACCOUNT_DETAILS.bankName}</p>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3">
                    <p className="text-slate-500">Account Number</p>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-slate-800">{PAYMENT_ACCOUNT_DETAILS.accountNumber}</p>
                      <button
                        className="text-emerald-600 hover:text-emerald-700"
                        onClick={() => handleCopy(PAYMENT_ACCOUNT_DETAILS.accountNumber, 'Account number copied.')}
                        aria-label="Copy account number"
                      >
                        <DocumentDuplicateIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 p-4 space-y-3">
                <h4 className="font-semibold text-slate-800">Upload transfer proof</h4>
                <p className="text-xs text-slate-500">Image will be optimized before secure upload.</p>

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelection}
                  disabled={isCompressing || isUploading}
                  className="block w-full text-sm text-slate-700 file:mr-4 file:rounded-md file:border-0 file:bg-emerald-50 file:px-3 file:py-2 file:text-emerald-700 hover:file:bg-emerald-100"
                />

                {optimizedFile && optimizedPreviewUrl && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs font-medium text-slate-600 mb-2">Proof of Payment Preview</p>
                    <div className="flex items-center gap-3">
                      <img
                        src={optimizedPreviewUrl}
                        alt="Optimized transfer proof"
                        className="h-14 w-14 rounded object-cover border border-slate-200"
                      />
                      <div>
                        <p className="text-xs text-slate-500 truncate max-w-[180px]">{selectedFile?.name}</p>
                        <p className="text-sm font-semibold text-emerald-700">
                          {Math.max(1, Math.round(optimizedFile.size / 1024))}KB
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {(isCompressing || isUploading) && (
                  <div className="inline-flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2">
                    <ArrowPathIcon className="h-4 w-4 animate-spin text-emerald-600" />
                    <p className="text-sm font-medium text-emerald-700">
                      {isCompressing ? 'Optimizing proof...' : 'Uploading proof...'}
                    </p>
                  </div>
                )}
                {uploadValidationMessage && (
                  <p className="text-xs text-red-600 font-medium">{uploadValidationMessage}</p>
                )}

                {!isCloudinaryConfigured && (
                  <p className="text-xs text-amber-600">
                    Configure cloud settings before you proceed.
                  </p>
                )}

                {proofUrl && (
                  <a href={proofUrl} target="_blank" rel="noreferrer" className="text-sm text-emerald-600 underline break-all pl-3">
                    View uploaded proof
                  </a>
                )}
              </div>

              <button
                onClick={handleSubmitToWhatsApp}
                className="w-full rounded-md bg-green-600 px-4 py-3 text-sm font-semibold text-white hover:bg-green-700"
              >
                Submit Order Now
              </button>
              {submitValidationMessage && (
                <p className="text-xs text-red-600 font-medium text-center">{submitValidationMessage}</p>
              )}
              <p className="text-xs text-slate-500 text-center">
                After submission, you will be redirected to our customer care agent to confirm your purchase.
              </p>
            </>
          )}

          {statusMessage && (
            <div className="flex items-start gap-2 rounded-lg border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-700">
              <CheckCircleIcon className="h-5 w-5 shrink-0" />
              <span>{statusMessage}</span>
            </div>
          )}

          {errorMessage && <p className="rounded-lg border border-red-100 bg-red-50 p-3 text-sm text-red-600">{errorMessage}</p>}
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
